export type SupportedLanguage =
  | 'ja'
  | 'en'
  | 'ko'
  | 'zh-TW'
  | 'zh-CN'
  | 'th'
  | 'ru'
  | 'fr'
  | 'de'
  | 'pt'
  | 'es'
  | 'id'
  | 'vi'
  | 'it';

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  ja: '日本語',
  en: 'English',
  ko: '한국어（韓国語）',
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  th: 'ภาษาไทย（タイ語）',
  ru: 'Русский（ロシア語）',
  fr: 'Français（フランス語）',
  de: 'Deutsch（ドイツ語）',
  pt: 'Português（ポルトガル語・ブラジル）',
  es: 'Español（スペイン語・スペイン）',
  id: 'Bahasa Indonesia（インドネシア語）',
  vi: 'Tiếng Việt（ベトナム語）',
  it: 'Italiano（イタリア語）',
};

export const ALL_LANGUAGES: SupportedLanguage[] = [
  'ja', 'en', 'ko', 'zh-TW', 'zh-CN', 'th', 'ru', 'fr', 'de', 'pt', 'es', 'id', 'vi', 'it',
];

/** Google locale per language, for the "open in Google" links. Mirrors the
 *  gl / hl / googleDomain fields of functions/src/types.ts LOCALES. */
export const SEARCH_LOCALES: Record<SupportedLanguage, { gl: string; hl: string; googleDomain: string }> = {
  ja: { gl: 'jp', hl: 'ja', googleDomain: 'google.co.jp' },
  en: { gl: 'us', hl: 'en', googleDomain: 'google.com' },
  ko: { gl: 'kr', hl: 'ko', googleDomain: 'google.co.kr' },
  'zh-TW': { gl: 'tw', hl: 'zh-tw', googleDomain: 'google.com.tw' },
  'zh-CN': { gl: 'cn', hl: 'zh-cn', googleDomain: 'google.com' },
  th: { gl: 'th', hl: 'th', googleDomain: 'google.co.th' },
  ru: { gl: 'ru', hl: 'ru', googleDomain: 'google.ru' },
  fr: { gl: 'fr', hl: 'fr', googleDomain: 'google.fr' },
  de: { gl: 'de', hl: 'de', googleDomain: 'google.de' },
  pt: { gl: 'br', hl: 'pt-br', googleDomain: 'google.com.br' },
  es: { gl: 'es', hl: 'es', googleDomain: 'google.es' },
  id: { gl: 'id', hl: 'id', googleDomain: 'google.co.id' },
  vi: { gl: 'vn', hl: 'vi', googleDomain: 'google.com.vn' },
  it: { gl: 'it', hl: 'it', googleDomain: 'google.it' },
};

/** Google search URL matching how the pipeline queries Serper for this
 *  language. pws=0 turns off personalization so the page is closer to what
 *  the tool recorded — it can still differ (ranks shift through the day). */
export function googleSearchUrl(query: string, language: SupportedLanguage): string {
  const { gl, hl, googleDomain } = SEARCH_LOCALES[language];
  const params = new URLSearchParams({ q: query, hl, gl, pws: '0' });
  return `https://www.${googleDomain}/search?${params.toString()}`;
}

/** Autocomplete fan-out methods configurable per language. Mirrors
 *  functions/src/types.ts InputMethod. */
export type InputMethod =
  | 'alphabet'
  | 'hiragana'
  | 'hangul'
  | 'bopomofo'
  | 'cangjie'
  | 'thai'
  | 'cyrillic';

export const INPUT_METHOD_LABELS: Record<InputMethod, string> = {
  alphabet: 'アルファベット (a–z)',
  hiragana: '五十音 (あ–ん)',
  hangul: 'ハングル (가–하)',
  bopomofo: '注音 (ㄅ–ㄦ)',
  cangjie: '倉頡 (24字)',
  thai: 'タイ文字 (ก–ฮ)',
  cyrillic: 'キリル文字 (а–я)',
};

interface InputMethodConfig {
  available: InputMethod[];
  defaults: InputMethod[];
}

/** Mirror of functions/src/services/input-methods.ts INPUT_METHODS_PER_LANGUAGE,
 *  used by the form to decide which checkboxes to render and which to lock. */
export const INPUT_METHODS_PER_LANGUAGE: Record<SupportedLanguage, InputMethodConfig> = {
  en: { available: ['alphabet'], defaults: ['alphabet'] },
  ja: { available: ['alphabet', 'hiragana'], defaults: ['alphabet'] },
  ko: { available: ['hangul', 'alphabet'], defaults: ['hangul'] },
  'zh-TW': { available: ['alphabet', 'bopomofo', 'cangjie'], defaults: ['alphabet'] },
  'zh-CN': { available: ['alphabet'], defaults: ['alphabet'] },
  th: { available: ['thai'], defaults: ['thai'] },
  ru: { available: ['cyrillic'], defaults: ['cyrillic'] },
  fr: { available: ['alphabet'], defaults: ['alphabet'] },
  de: { available: ['alphabet'], defaults: ['alphabet'] },
  pt: { available: ['alphabet'], defaults: ['alphabet'] },
  es: { available: ['alphabet'], defaults: ['alphabet'] },
  id: { available: ['alphabet'], defaults: ['alphabet'] },
  vi: { available: ['alphabet'], defaults: ['alphabet'] },
  it: { available: ['alphabet'], defaults: ['alphabet'] },
};

/** Per-language cap on user-specified keywords. Mirrors
 *  functions/src/services/pipeline.ts MAX_CUSTOM_KEYWORDS. */
export const MAX_CUSTOM_KEYWORDS = 50;

/** Selectable run cadences. The cron itself fires daily at 09:00 JST and skips
 *  any game whose `intervalDays` haven't elapsed since `lastScheduledRunAt`. */
export const INTERVAL_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: '毎日' },
  { value: 2, label: '2日に1回' },
  { value: 3, label: '3日に1回' },
  { value: 7, label: '1週間に1回' },
  { value: 14, label: '2週間に1回' },
  { value: 30, label: '1ヶ月に1回' },
];

export function intervalLabel(intervalDays: number): string {
  return INTERVAL_OPTIONS.find((o) => o.value === intervalDays)?.label ?? `${intervalDays}日に1回`;
}

export interface GameSettings {
  id: string;
  title: string;
  titleByLanguage?: Partial<Record<SupportedLanguage, string>>;
  /** Per-language short forms (e.g. "ポケチャン"). Each runs an independent a–z fan-out and AI filter, then results are merged by suffix. */
  aliasesByLanguage?: Partial<Record<SupportedLanguage, string[]>>;
  /** Per-language fan-out methods. Absent or empty falls back to defaults. */
  inputMethodsByLanguage?: Partial<Record<SupportedLanguage, InputMethod[]>>;
  /** Per-language user-specified keywords, entered without the game name —
   *  functions search "{title} {word}". No AI filter or scoring (translation
   *  IS applied). Ranks show up in RunResult.customResults. */
  customKeywordsByLanguage?: Partial<Record<SupportedLanguage, string[]>>;
  /** Other sites' domains tracked next to gamewith's (all languages).
   *  Stored normalized — see utils/domain.ts normalizeDomain. */
  competitorDomains?: string[];
  languages: SupportedLanguage[];
  maxKeywords: number;
  intervalDays: number;
  enabled: boolean;
  createdAt?: { seconds: number; nanoseconds: number } | Date;
  updatedAt?: { seconds: number; nanoseconds: number } | Date;
  lastRunAt?: { seconds: number; nanoseconds: number } | Date | null;
  lastScheduledRunAt?: { seconds: number; nanoseconds: number } | Date | null;
  lastRunSummary?: Array<{
    language: SupportedLanguage;
    keywordCount: number;
    topKeyword: string | null;
  }>;
}

export interface KeywordRanking {
  position: number;
  url: string;
  title: string;
}

/** Gamewith hosts we track rankings for — drives table columns, chart lines
 *  and legends. Mirrors functions/src/types.ts GAMEWITH_HOSTS. */
export const GAMEWITH_HOSTS = ['gamewith.ai', 'gamewith.jp'] as const;
export type GamewithHost = (typeof GAMEWITH_HOSTS)[number];

/** Per-host gamewith positions for one query. null = 圏外 (not ranked). */
export type GamewithRankings = Record<GamewithHost, number | null>;

/** Per-domain positions keyed by normalized competitor domain. null = 圏外;
 *  a missing key = not tracked in that run. Mirrors functions/src/types.ts. */
export type DomainRankings = Record<string, number | null>;

/** Days of rank points kept per keyword. UI copy only — the authoritative
 *  pruning constant is functions/src/services/rank-history.ts
 *  RANK_HISTORY_RETENTION_DAYS; keep the two in sync. */
export const RANK_HISTORY_RETENTION_DAYS = 180;

export interface KeywordResult {
  keyword: string;
  /** `keyword` with the matching base prefix stripped — what the dashboard
   *  shows as the primary label. Falls back to keyword when not present. */
  displayKeyword?: string;
  /** Japanese translation of the display keyword (non-JA tabs only). */
  translatedKeyword?: string;
  score: number;
  totalResults: number;
  topResults: KeywordRanking[];
  gamewithRankings: GamewithRankings;
  /** Rankings from the keyword's last recorded day before this run (its most
   *  recent rank-history point) — the badge base. Absent when the keyword has
   *  no earlier history (or the run predates this feature). */
  previousGamewithRankings?: GamewithRankings;
  /** Competitor domain positions (absent when none were configured). */
  competitorRankings?: DomainRankings;
  /** competitorRankings of the badge-base history point. */
  previousCompetitorRankings?: DomainRankings;
  /** Per-base breakdown when multiple bases (title + aliases) hit the same suffix. */
  sources?: Array<{
    base: string;
    keyword: string;
    score: number;
  }>;
}

export interface RunResult {
  gameId: string;
  gameTitle: string;
  language: SupportedLanguage;
  executedAt: string;
  keywordCount: number;
  results: KeywordResult[];
  /** User-specified keywords, shown separately from auto-selected `results`.
   *  Not scored (`score` is always 0) — the table hides the score column.
   *  Absent in run JSON written before this feature. */
  customResults?: KeywordResult[];
}

/** One dated sample of a keyword's gamewith rankings (JST date). Mirrors
 *  functions/src/types.ts RankHistoryPoint. */
export interface RankHistoryPoint {
  date: string;
  rankings: GamewithRankings;
  competitorRankings?: DomainRankings;
}

/** Which keyword set an AI analysis ran over. Mirrors functions/src/types.ts. */
export type AnalyzeScope = 'ranked' | 'all';

/** Execution log of one AI analysis (games/{gameId}/aiAnalyses/{id}).
 *  Mirrors functions/src/types.ts AiAnalysis. */
export interface AiAnalysis {
  id: string;
  language: SupportedLanguage;
  scope: AnalyzeScope;
  prompt: string;
  resultText: string;
  model: string;
  keywordCount: number;
  suggestionCount: number;
  durationMs: number;
  createdBy: string;
  /** True when this entry records a failure (resultText is the error notice). */
  isError?: boolean;
  createdAt?: { seconds: number; nanoseconds: number } | Date;
}

/** Per-keyword rank time series document at
 *  games/{gameId}/rankHistory/{docId} (points oldest first, ≤180 days).
 *  Mirrors functions/src/types.ts RankHistoryDoc. */
export interface RankHistoryDoc {
  keyword: string;
  language: SupportedLanguage;
  lastSeen: string;
  updatedAt: string;
  points: RankHistoryPoint[];
}
